/**
 * Coordinate frames and the one conversion between them.
 *
 * ## Why two frames
 *
 * The flight model is written in **standard aerospace axes**, because every equation, sign
 * convention and coefficient definition in the aerodynamics literature assumes them:
 *
 *   - **Body frame (FRD)**: `x` forward out the nose, `y` right out the starboard wing,
 *     `z` down through the belly. Roll `p` about x, pitch `q` about y, yaw `r` about z.
 *     `alpha = atan2(w, u)` and `beta = asin(v / V)` are only correct in this frame.
 *   - **World frame (NED)**: `x` north, `y` east, `z` **down**. Gravity is `+z`. Altitude is
 *     `−z`. Headings are `atan2(east, north)`, which is what a compass reads.
 *
 * Three.js renders in a Y-up, right-handed frame where the aircraft model's nose points −Z.
 * Rather than contort the physics, the FDM stays in NED/FRD and converts once, at the render
 * boundary, through {@link aeroToRenderVector} and {@link aeroToRenderQuaternion}.
 *
 * ## The conversion
 *
 * Both conversions are the *same* change of basis `C`, because body→render and world→render
 * relate the same pair of axis conventions:
 *
 *     render.x =  aero.y     (east / right)
 *     render.y = −aero.z     (up = −down)
 *     render.z = −aero.x     (south = −north / aft = −forward)
 *
 * `C` is a proper rotation (det = +1): a 120° turn about `(1, 1, −1)/√3`, which as a
 * quaternion is `(½, ½, −½, ½)`. An attitude quaternion therefore converts by the similarity
 * transform `q_render = qC ⊗ q ⊗ qC*`, matching `R_render = C R Cᵀ`. Both forms are asserted
 * against each other in the tests, because a silent handedness error here would poison
 * everything downstream and look merely "a bit wrong".
 */

import { Quaternion, Vector3 } from 'three';

/** Change-of-basis quaternion from aerospace axes (NED/FRD) to render axes (Y-up). */
export const AERO_TO_RENDER_QUAT = /* #__PURE__ */ new Quaternion(0.5, 0.5, -0.5, 0.5);

/** Inverse of {@link AERO_TO_RENDER_QUAT}. */
export const RENDER_TO_AERO_QUAT = /* #__PURE__ */ new Quaternion(-0.5, -0.5, 0.5, 0.5);

const scratchQuat = /* #__PURE__ */ new Quaternion();

/**
 * Convert a vector from aerospace axes (NED or FRD) to render axes.
 * Safe to call with `out === source`. Allocation-free.
 */
export function aeroToRenderVector(source: Vector3, out: Vector3): Vector3 {
  const { x, y, z } = source;
  return out.set(y, -z, -x);
}

/** Convert a vector from render axes back to aerospace axes. Allocation-free. */
export function renderToAeroVector(source: Vector3, out: Vector3): Vector3 {
  const { x, y, z } = source;
  return out.set(-z, x, -y);
}

/**
 * Convert an attitude quaternion (body→NED) into a render-space rotation (model→render).
 * Allocation-free; `out` may alias `source`.
 */
export function aeroToRenderQuaternion(source: Quaternion, out: Quaternion): Quaternion {
  // q_render = qC ⊗ q ⊗ qC*
  scratchQuat.copy(AERO_TO_RENDER_QUAT).multiply(source);
  return out.copy(scratchQuat).multiply(RENDER_TO_AERO_QUAT);
}

/**
 * Euler angles of an attitude quaternion, in the aerospace 3-2-1 (yaw→pitch→roll) sequence.
 *
 * This is the sequence every attitude indicator, autopilot and flight log uses: heading first
 * about `z` (down), then pitch about the new `y`, then roll about the new `x`. Writing it out
 * by hand rather than going through three's Euler orders keeps the convention explicit and
 * removes any doubt about which axis is which.
 *
 * Returns radians: heading in [0, 2π), pitch in [−π/2, π/2], roll in (−π, π].
 * Allocation-free — writes into `out`.
 */
export interface EulerAngles {
  /** Heading / yaw ψ [rad], measured clockwise from north. */
  headingRad: number;
  /** Pitch θ [rad], nose-up positive. */
  pitchRad: number;
  /** Roll φ [rad], right-wing-down positive. */
  rollRad: number;
}

export function quaternionToEuler(q: Quaternion, out: EulerAngles): EulerAngles {
  const { x, y, z, w } = q;

  // sin(pitch) from the rotation matrix element R31, clamped against round-off at the poles.
  let sinPitch = 2 * (w * y - z * x);
  sinPitch = sinPitch > 1 ? 1 : sinPitch < -1 ? -1 : sinPitch;
  out.pitchRad = Math.asin(sinPitch);

  if (Math.abs(sinPitch) > 0.999999) {
    // Gimbal lock: heading and roll are degenerate. Put the whole rotation into heading, which
    // is what an attitude indicator does when the nose is straight up or straight down.
    out.headingRad = 2 * Math.atan2(x, w);
    out.rollRad = 0;
  } else {
    out.rollRad = Math.atan2(2 * (w * x + y * z), 1 - 2 * (x * x + y * y));
    out.headingRad = Math.atan2(2 * (w * z + x * y), 1 - 2 * (y * y + z * z));
  }

  if (out.headingRad < 0) out.headingRad += Math.PI * 2;
  return out;
}

/** Build an attitude quaternion from aerospace Euler angles (3-2-1: heading, pitch, roll). */
export function eulerToQuaternion(
  headingRad: number,
  pitchRad: number,
  rollRad: number,
  out: Quaternion,
): Quaternion {
  const cy = Math.cos(headingRad * 0.5);
  const sy = Math.sin(headingRad * 0.5);
  const cp = Math.cos(pitchRad * 0.5);
  const sp = Math.sin(pitchRad * 0.5);
  const cr = Math.cos(rollRad * 0.5);
  const sr = Math.sin(rollRad * 0.5);

  return out.set(
    cy * cp * sr - sy * sp * cr, // x
    sy * cp * sr + cy * sp * cr, // y
    sy * cp * cr - cy * sp * sr, // z
    cy * cp * cr + sy * sp * sr, // w
  );
}

/** Create a zeroed {@link EulerAngles} struct. */
export function makeEulerAngles(): EulerAngles {
  return { headingRad: 0, pitchRad: 0, rollRad: 0 };
}
